import React, { useCallback, useState, useRef } from 'react';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { useTranslation } from 'react-i18next';
import type { ProductionScheduleDto, CampaignData, DoughBatchData, OvenBatchData } from '../../api';
import { WizardProvider, type RoutingValidationIssue } from '../../contexts/ProductionScheduleWizardContext';
import { useWizardContext } from '../../contexts/useWizardContext';
import { WizardStepRenderer } from '../../components/wizard';
import { buildIntermediateOrders } from '../../utils/buildIntermediateOrders';

export type ProductionScheduleWizardProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (schedule: ProductionScheduleDto) => Promise<void>;
};

type WizardStep = {
  id: number;
  title: string;
  description: string;
  isCompleted: boolean;
};

// RoutingValidationIssue is now imported from WizardContext

// Inner component that uses the wizard context
const ProductionScheduleWizardContent: React.FC<ProductionScheduleWizardProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  const { t } = useTranslation(['common']);
  const wizardContext = useWizardContext();
  const { currentStep, handleNext, handlePrevious, formData, batchDeterminationData, materials, sequenceEntries, sequenceOptimization, setCampaignsWithoutRoutingToZero, getPreviewScheduleUuid, planningMode, setPlanningMode, sourceScheduleUuid, frozenZoneEnd, step0ReleasedPlansLoading, step0HasReleasedPlans, isLoadingDemands, isLoadingSettings, forecasts, pendingForecasts } = wizardContext;
  const routingValidationIssues = wizardContext.routingValidationIssues ?? null;
  const [isValidatingRouting, setIsValidatingRouting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const saveJustFailedRef = useRef(false);

  // Handlers via ref for footerTemplate: Syncfusion may render the footer outside the React tree; clicks call the current handlers through the refs.
  const handleNextRef = useRef(handleNext);
  const handlePreviousRef = useRef(handlePrevious);
  handleNextRef.current = handleNext;
  handlePreviousRef.current = handlePrevious;

  const [step0DisplayMode, setStep0DisplayMode] = useState<'NEW_PLANNING' | 'CONTINUATION' | 'REPLANNING'>(planningMode);
  React.useEffect(() => {
    setStep0DisplayMode(planningMode);
  }, [planningMode]);
  const handleStep0SelectMode = useCallback((mode: 'NEW_PLANNING' | 'CONTINUATION' | 'REPLANNING') => {
    setStep0DisplayMode(mode);
    setPlanningMode(mode);
  }, [setPlanningMode]);

  // Validation now happens in WizardContext, no longer here


  const steps: WizardStep[] = [
    {
      id: 1,
      title: t('productionSchedule.wizard.step0.title', 'Planning Type'),
      description: t('productionSchedule.wizard.step0.description', 'Choose new planning, continuation or replanning'),
      isCompleted: false
    },
    {
      id: 2,
      title: t('productionSchedule.wizard.step1.title', 'Demands (Determine production quantities)'),
      description: t('productionSchedule.wizard.step1.description', 'Determine production quantities for the planning horizon'),
      isCompleted: false
    },
    {
      id: 3,
      title: t('productionSchedule.wizard.step2.sequenceTitle', 'Campaign Sequence'),
      description: t('productionSchedule.wizard.step2.campaignDescription', 'Define preferred order of campaigns'),
      isCompleted: false
    },
    {
      id: 4,
      title: t('productionSchedule.wizard.step3.batchTitle', 'Batch Determination'),
      description: t('productionSchedule.wizard.step3.batchDescription', 'Determine production batches for selected demands'),
      isCompleted: false
    },
    {
      id: 5,
      title: t('productionSchedule.wizard.step4.reviewTitle', 'Summary'),
      description: t('productionSchedule.wizard.step4.reviewDescription', 'Sequence, assigned quantities and bake-out preview'),
      isCompleted: false
    }
  ];

  const hasValidPlanningMode = planningMode === 'NEW_PLANNING' || planningMode === 'CONTINUATION' || planningMode === 'REPLANNING';
  // For fresh planning do not wait for the released schedules to load; for follow-up/replanning we do (the dropdown/base needs them).
  const canProceedFromStep0 =
    hasValidPlanningMode
    && (planningMode === 'NEW_PLANNING' || !step0ReleasedPlansLoading)
    && (planningMode !== 'CONTINUATION' || step0HasReleasedPlans)
    && (planningMode !== 'REPLANNING' || !!sourceScheduleUuid);
  // Replanning step 1: the frozen zone end is a mandatory field.
  const canProceedFromStep1 = planningMode !== 'REPLANNING' || !!frozenZoneEnd;

  const isDataLoading = isLoadingDemands || isLoadingSettings || materials.length === 0;

  const handleSave = async () => {
    if (isSaving) return;
    try {
      // Enforce validation on save as well (strict workflow)
      const step3Validation = wizardContext.validateStep3();
      if (!step3Validation.isValid) {
        wizardContext.setCurrentStep(3);
        return;
      }
      if (routingValidationIssues && routingValidationIssues.length > 0) {
        wizardContext.setCurrentStep(3);
        return;
      }

      // Planning horizon at day granularity: always the local date at 00:00 (start) or 23:59:59 (end), never UTC/toISOString, so that no time zone offset (for example 1 hour) creeps in.
      const ensureDateOnlyStart = (dateValue: string | undefined, defaultValue: Date): string => {
        const date = !dateValue ? defaultValue : (dateValue.includes('T') ? new Date(dateValue) : new Date(dateValue + 'T12:00:00'));
        if (isNaN(date.getTime())) return `${defaultValue.getFullYear()}-${String(defaultValue.getMonth() + 1).padStart(2, '0')}-${String(defaultValue.getDate()).padStart(2, '0')}T00:00:00`;
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}T00:00:00`;
      };
      const ensureDateOnlyEnd = (dateValue: string | undefined, defaultValue: Date): string => {
        const date = !dateValue ? defaultValue : (dateValue.includes('T') ? new Date(dateValue) : new Date(dateValue + 'T12:00:00'));
        if (isNaN(date.getTime())) {
          const def = defaultValue;
          return `${def.getFullYear()}-${String(def.getMonth() + 1).padStart(2, '0')}-${String(def.getDate()).padStart(2, '0')}T23:59:59`;
        }
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}T23:59:59`;
      };

      // Ensure plannedEnd is always greater than plannedStart (required by database constraint)
      const plannedStartDate = ensureDateOnlyStart(formData.plannedStart, new Date());
      let plannedEndDate = ensureDateOnlyEnd(formData.plannedEnd, new Date(Date.now() + 1 * 24 * 60 * 60 * 1000));
      
      // If end date is not greater than start date, set it to end of start date
      if (new Date(plannedEndDate) <= new Date(plannedStartDate)) {
        const startDate = new Date(plannedStartDate);
        const y = startDate.getFullYear();
        const m = String(startDate.getMonth() + 1).padStart(2, '0');
        const d = String(startDate.getDate()).padStart(2, '0');
        plannedEndDate = `${y}-${m}-${d}T23:59:59`;
      }

      // Build campaign data from batchDeterminationData
      // IMPORTANT: production orders are now generated automatically in the backend
      const campaigns: CampaignData[] = [];
      
      // Collect all campaigns from batchDeterminationData
      Object.values(batchDeterminationData).forEach((batchData: any) => {
        if (!batchData.campaigns || batchData.campaigns.length === 0) {
          return; // no campaigns for this material
        }

        // Convert frontend campaigns to backend CampaignData
        batchData.campaigns.forEach((campaign: any) => {
          // Convert dough batches (guard: can be [] after the 'set quantities to zero' action)
          const doughBatches: DoughBatchData[] = (campaign.doughBatches || []).map((db: any) => ({
            uuid: db.uuid,
            sequenceNumber: db.sequenceNumber,
            quantity: db.quantity,
            doughQuantityKg: db.doughQuantityKg
          }));

          // Convert oven batches (when present)
          const ovenBatches: OvenBatchData[] | undefined = (campaign.ovenBatches && campaign.ovenBatches.length > 0)
            ? campaign.ovenBatches.map((ob: any) => ({
                uuid: ob.uuid,
                ovenNumber: ob.ovenNumber,
                utilization: ob.utilization,
                pieces: ob.pieces,
                piecesPerOven: ob.piecesPerOven
              }))
            : undefined;

          // Create the CampaignData
          const campaignData: CampaignData = {
            campaignId: campaign.campaignId,
            sequenceNumber: campaign.sequenceNumber,
            materialId: campaign.materialId,
            desiredQuantity: campaign.desiredQuantity,
            totalBakedQuantity: campaign.totalBakedQuantity,
            dueDate: campaign.dueDate,
            doughBatches: doughBatches.length > 0 ? doughBatches : undefined,
            ovenBatches: ovenBatches && ovenBatches.length > 0 ? ovenBatches : undefined
          };

          campaigns.push(campaignData);
        });
      });

      // Sort campaigns by sequenceNumber
      campaigns.sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0));

      // Planning type from wizard choice (persisted; backend uses it instead of computing from overlaps)
      const planningType = planningMode === 'REPLANNING' ? 'REPLANNING' : planningMode;

      // sequenceSnapshot = the sequence selected in step 2 (section 7.8.7) - for a later replanning.
      // maxBatches is stored with it so that REPLANNING can reconstruct the campaign split.
      const sequenceSnapshot = sequenceOptimization
        ? JSON.stringify({ optimized: true })
        : sequenceEntries && sequenceEntries.length > 0
          ? JSON.stringify({
              campaigns: sequenceEntries
                .filter((e: any) => e.productId)
                .sort((a: any, b: any) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0))
                .map((e: any, i: number) => ({
                  materialId: e.productId,
                  sequenceNumber: i + 1,
                  ...(e.maxBatches != null ? { maxBatches: e.maxBatches } : {})
                }))
            })
          : undefined;

      // Prepare the schedule data with all required fields
      const scheduleData: ProductionScheduleDto = {
        ...formData,
        uuid: getPreviewScheduleUuid?.() ?? undefined,
        scheduleName: ('scheduleName' in formData ? formData.scheduleName : undefined) || `Produktionsplan ${new Date().toLocaleDateString('de-DE')}`,
        plannedStart: plannedStartDate,
        plannedEnd: plannedEndDate,
        isReleased: ('isReleased' in formData ? formData.isReleased : undefined) ?? false,
        status: ('status' in formData ? formData.status : undefined) || 'new',
        planVersion: ('planVersion' in formData ? formData.planVersion : undefined) ?? 1,
        planningType,
        sequenceSnapshot,
        sequenceOptimization: sequenceOptimization || undefined,
        // productionOrderIds is returned by the backend (after automatic generation)
        campaigns: campaigns.length > 0 ? campaigns : undefined,
        // Replanning: the backend needs parentScheduleId and frozenZoneEnd to detect a replan (request.now = plan start, fixed windows)
        ...(planningMode === 'REPLANNING' && sourceScheduleUuid ? { parentScheduleId: sourceScheduleUuid } : {}),
        ...(planningMode === 'REPLANNING' && frozenZoneEnd ? { frozenZoneEnd } : {}),
        // Intermediate product orders: the frontend computes the splitting (maxLotSize / minLotSize /
        // lotSizeMultiple) here directly from the forecasts + intermediate product master data - identical to the
        // helper call in Step4Review used for the display. The backend persists the list 1:1 (createOrders
        // with orderNumber). An empty array -> the backend uses the legacy path createOrdersFromDemands
        // (1 forecast -> 1 order, without splitting).
        intermediateOrders: buildIntermediateOrders({
          forecasts: [...(forecasts ?? []), ...(pendingForecasts ?? [])],
          materials: (materials ?? []) as any,
          planStart: plannedStartDate?.split('T')[0] ?? null,
          planEnd: (plannedEndDate ?? plannedStartDate)?.split('T')[0] ?? null,
          schedulePrefix: (getPreviewScheduleUuid?.() ?? '').replace(/-/g, '').substring(0, 8),
        }).saveOrders,
        generateAutoIntermediateOrders: false
      };

      setIsSaving(true);
      saveJustFailedRef.current = false;
      // Save the production schedule (the backend creates the production orders automatically)
      await onSave(scheduleData);
      onClose();
      // After close: component unmounts — no further state updates to avoid
      // Syncfusion property refresh on partially destroyed components.
    } catch (error) {
      console.error('Error saving production schedule:', error);
      saveJustFailedRef.current = true;
      // Error toast already shown by CrudPage's handleError; wizard stays open so user can fix the issue
      setIsValidatingRouting(false);
      setIsSaving(false);
    }
  };

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  React.useEffect(() => {
    wizardContext.registerDirectStartSave?.(() => handleSaveRef.current());
    return () => wizardContext.registerDirectStartSave?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only depends on registerDirectStartSave function, not entire wizardContext object
  }, [wizardContext.registerDirectStartSave]);

  const footerTemplate = () => (
    <div className="flex justify-between p-4">
      <ButtonComponent
        onClick={() => handlePreviousRef.current?.()}
        disabled={currentStep === 0}
        cssClass="e-outline"
      >
        {t('actions.previous')}
      </ButtonComponent>
      <div className="flex gap-2">
        <ButtonComponent
          onClick={() => onClose()}
          cssClass="e-outline"
        >
          {t('actions.cancel')}
        </ButtonComponent>
        {currentStep < 4 && (
          <ButtonComponent
            onClick={() => handleNextRef.current?.()}
            cssClass="e-primary"
            disabled={(currentStep === 0 && !canProceedFromStep0) || (currentStep === 1 && !canProceedFromStep1) || isDataLoading}
          >
            {t('actions.next')}
          </ButtonComponent>
        )}
        {currentStep === 4 ? (
          <ButtonComponent
            onClick={handleSave}
            cssClass="e-danger"
            disabled={isValidatingRouting || isSaving}
          >
            {t('productionSchedule.wizard.startPlanning', 'Start planning')}
          </ButtonComponent>
        ) : null}
      </div>
    </div>
  );

  return (
    <DialogComponent
      visible={isOpen}
      width="1550px"
      height="1000px"
      header={t('productionSchedule.wizard.title')}
      showCloseIcon={true}
      closeOnEscape={true}
      cssClass="production-schedule-wizard"
      beforeClose={(e: any) => {
        if (saveJustFailedRef.current) {
          e.cancel = true;
          saveJustFailedRef.current = false;
          return;
        }
        onClose();
      }}
      footerTemplate={footerTemplate}
    >
      <div className="h-full flex flex-col">
        {/* Progress Steps */}
        <div className="border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  currentStep >= index
                    ? 'bg-[#09584b] border-[#09584b] text-white'
                    : 'bg-white border-gray-300 text-gray-500'
                }`}>
                  {step.id}
                </div>
                <div className="ml-2">
                  <div className={`text-sm font-medium ${
                    currentStep >= index ? 'text-[#09584b]' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </div>
                  <div className="text-xs text-gray-400">
                    {step.description}
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-0.5 mx-4 ${
                    currentStep > index ? 'bg-[#09584b]' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content: flex so step 1 grid can fill height */}
        <div className="flex-1 min-h-0 flex flex-col px-2 pb-2 pt-4 overflow-hidden">
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <WizardStepRenderer
              currentStep={currentStep}
              step0PlanningMode={{
                planningMode,
                selectedMode: step0DisplayMode,
                onPlanningModeSelect: handleStep0SelectMode,
                sourceScheduleUuid,
                frozenZoneEnd
              }}
            />
          </div>
        </div>
      </div>

      {routingValidationIssues && routingValidationIssues.length > 0 && currentStep === 3 && (
        <DialogComponent
          visible={true}
          width="700px"
          header={t('productionSchedule.wizard.step3.validation.title', 'Validation errors')}
          showCloseIcon={true}
          closeOnEscape={true}
          close={() => {
            // Clear validation issues - managed in the context
            // setRoutingValidationIssues(null);
          }}
          footerTemplate={() => (
            <div className="flex justify-end gap-2 p-2">
              <ButtonComponent
                cssClass="e-outline"
                onClick={() => {
                  // Close the dialog - validation errors remain, the step is not changed
                }}
              >
                {t('actions.cancel', 'Abbrechen')}
              </ButtonComponent>
              <ButtonComponent
                cssClass="e-primary"
                onClick={() => {
                  setCampaignsWithoutRoutingToZero(routingValidationIssues.map((issue: RoutingValidationIssue) => issue.materialId));
                }}
              >
                {t('productionSchedule.wizard.step3.validation.setQuantitiesToZero', 'Set quantities to zero and lock fields')}
              </ButtonComponent>
            </div>
          )}
        >
          <div className="p-4 space-y-3">
            <p className="text-gray-700">
              {t(
                'productionSchedule.wizard.step3.validation.message',
                'Für die folgenden Kampagnen wurde kein gültiger aktiver Arbeitsplan gefunden. Möchten Sie die Kampagnen löschen oder abbrechen?'
              )}
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              {routingValidationIssues.map((issue: RoutingValidationIssue) => {
                let reasonText = '';
                if (issue.reason === 'missingRouting') {
                  reasonText = ` — ${t('productionSchedule.wizard.step3.validation.noRouting', 'No active routing available')}`;
                } else if (issue.reason === 'incompleteRouting') {
                  reasonText = ` — ${t('productionSchedule.wizard.step3.validation.incompleteRouting', 'Routing is not complete')}`;
                } else {
                  reasonText = ` — ${t('productionSchedule.wizard.step3.validation.missingProcessingLine', 'Routing without processing line')}`;
                }
                return (
                  <li key={issue.materialId}>
                    {issue.materialName}{reasonText}
                  </li>
                );
              })}
            </ul>
          </div>
        </DialogComponent>
      )}
    </DialogComponent>
  );
};

// Main component that provides the wizard context
const ProductionScheduleWizardDialog: React.FC<ProductionScheduleWizardProps> = (props) => {
  // Only render the provider and content when dialog is open
  // This ensures the context is properly initialized before children try to use it
  if (!props.isOpen) {
    return null;
  }
  
  return (
    <WizardProvider 
      isOpen={props.isOpen} 
      onClose={props.onClose}
    >
      <ProductionScheduleWizardContent {...props} />
    </WizardProvider>
  );
};

export default ProductionScheduleWizardDialog; 